import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Link
} from '@mui/material';
import {
  Hub as CtoIcon,
  Close as CloseIcon,
  Map as MapIcon,
  Circle
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { ftthCaixaService, FtthCaixa } from '../../../services/ftthCaixaService';
import CaixaPortsListModal from '../../../components/modals/CaixaPortsListModal';

interface CtosModalProps {
  open: boolean;
  onClose: () => void;
  cardSlot?: number;
  portNumber?: string;
  portId?: number;
  oltId?: string;
  sfp?: string;
}

const getCaixaStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
  switch (status) {
    case 'ativo':
      return 'success';
    case 'inativo':
      return 'error';
    case 'manutencao':
      return 'warning';
    case 'aguardando_instalacao':
      return 'warning';
    default:
      return 'default';
  }
};

const getCaixaStatusLabel = (status: string): string => {
  switch (status) {
    case 'ativo':
      return 'Ativo';
    case 'inativo':
      return 'Inativo';
    case 'manutencao':
      return 'Manutenção';
    case 'aguardando_instalacao':
      return 'Aguardando instalação';
    default:
      return status || 'Desconhecido';
  }
};

const getFuncionamentoColor = (funcionamento?: string) => {
  if (funcionamento === 'online') return '#4CAF50';
  if (funcionamento === 'offline') return '#F44336';
  return '#9E9E9E';
};

const getFuncionamentoLabel = (funcionamento?: string) => {
  if (funcionamento === 'online') return 'Online';
  if (funcionamento === 'offline') return 'Offline';
  return 'Vazia';
};

const CtosModal: React.FC<CtosModalProps> = ({
  open,
  onClose,
  cardSlot,
  portNumber,
  portId,
  oltId,
  sfp
}) => {
  const navigate = useNavigate();
  const [ctos, setCtos] = useState<FtthCaixa[]>([]);
  const [loading, setLoading] = useState(false);
  const [portsModal, setPortsModal] = useState<{ open: boolean; caixaId?: number; caixaName?: string }>({
    open: false
  });

  const fetchCtosByPortId = async (targetPortId: number) => {
    setLoading(true);
    try {
      const list = await ftthCaixaService.getCaixasByPortId(targetPortId);
      const sorted = [...list].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      setCtos(sorted);
    } catch {
      toast.error('Erro ao carregar CTOs');
      setCtos([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCtosByOltSfp = async (targetOltId: string, targetSfp: string) => {
    setLoading(true);
    try {
      const list = await ftthCaixaService.getCaixasByOltSfp(parseInt(targetOltId, 10), targetSfp);
      const sorted = [...list].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      setCtos(sorted);
    } catch {
      toast.error('Erro ao carregar CTOs');
      setCtos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCtos([]);
    onClose();
  };

  const handleOpenMap = (caixa: FtthCaixa) => {
    if (caixa.latitude == null || caixa.longitude == null) return;
    window.open(`https://www.google.com/maps?q=${caixa.latitude},${caixa.longitude}`, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    if (!open) return;
    if (portId) {
      fetchCtosByPortId(portId);
    } else if (oltId && sfp) {
      fetchCtosByOltSfp(oltId, sfp);
    }
  }, [open, portId, oltId, sfp]);

  const modalTitle = portId
    ? `CTOs da Porta ${portNumber ?? portId}${cardSlot != null ? ` - Slot ${cardSlot}` : ''} - Qtd: ${ctos.length}`
    : `CTOs do SFP ${sfp} - Qtd: ${ctos.length}`;

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ p: 1.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <CtoIcon color="primary" />
            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
              {modalTitle}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ p: 0.5 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 1.5, overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{
            minHeight: 200,
            maxHeight: '60vh',
            overflow: 'auto',
            '& .MuiTableCell-root': {
              py: 0.5,
              px: 1.5,
              fontSize: '0.875rem',
              lineHeight: 1.2,
              whiteSpace: 'nowrap'
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.8rem'
            },
            '& .MuiTableHead-root': {
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'background.paper',
              boxShadow: '0 1px 0 0 rgba(224, 224, 224, 1)'
            }
          }}>
            <Table sx={{ tableLayout: 'auto', minWidth: 'max-content' }} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>ID</strong></TableCell>
                  <TableCell><strong>Nome</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Funcionamento</strong></TableCell>
                  <TableCell><strong>Endereço</strong></TableCell>
                  <TableCell><strong>Portas em uso</strong></TableCell>
                  <TableCell><strong>Capacidade</strong></TableCell>
                  <TableCell><strong>SFP</strong></TableCell>
                  <TableCell align="center"><strong>Ações</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ctos.length > 0 ? (
                  ctos.map((cto) => (
                    <TableRow key={cto.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {cto.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          sx={{ fontSize: '0.8rem', textAlign: 'left' }}
                          onClick={() => {
                            navigate(`/ftth/caixas/${cto.id}`);
                            onClose();
                          }}
                        >
                          {cto.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getCaixaStatusLabel(cto.status)}
                          size="small"
                          color={getCaixaStatusColor(cto.status)}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={
                            <Circle
                              sx={{
                                color: '#fff !important',
                                fontSize: '10px !important'
                              }}
                            />
                          }
                          label={getFuncionamentoLabel(cto.funcionamento)}
                          size="small"
                          onClick={() => setPortsModal({ open: true, caixaId: cto.id, caixaName: cto.name })}
                          sx={{
                            color: '#fff',
                            bgcolor: getFuncionamentoColor(cto.funcionamento),
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            height: 20,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.85 },
                            '& .MuiChip-icon': { ml: 0.5 }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', maxWidth: 280, whiteSpace: 'normal' }}>
                          {cto.address || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {cto.portas_em_uso ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {cto.capacidade ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {cto.sfp || cto.pon_port?.sfp || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {cto.latitude != null && cto.longitude != null ? (
                          <IconButton
                            color="primary"
                            size="small"
                            aria-label="Abrir no mapa"
                            onClick={() => handleOpenMap(cto)}
                            sx={{ p: 0.5 }}
                          >
                            <MapIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <CtoIcon color="action" sx={{ fontSize: 48 }} />
                        <Typography variant="body1" color="text.secondary">
                          Nenhuma CTO encontrada
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Esta porta não possui caixas CTO vinculadas
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={handleClose} color="primary" size="small" sx={{ fontSize: '0.8rem' }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>

    <CaixaPortsListModal
      open={portsModal.open}
      onClose={() => setPortsModal({ open: false })}
      caixaId={portsModal.caixaId}
      caixaName={portsModal.caixaName}
    />
    </>
  );
};

export default CtosModal;
